// DS: MO0.3
import React, { useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import isNumber from 'lodash/isNumber';
import { URLS } from '@wix/da-url';
import { useUniqueId } from '@wix/da-hooks/pkg/useUniqueId';
import DollarSignIcon from '@wix/da-ds/pkg/Icons/16x16/DollarSign';
import TextInput, {
  type TextInputProps,
} from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import {
  MIN_PRICE_TO_ACCEPT_DOLLARS,
  MAX_CORE_MAX,
} from '../utils/profits';
import s from './DollarPriceInput.scss';

export interface Props
  extends Omit<
    TextInputProps,
    | 'onChange'
    | 'step'
    | 'id'
    | 'autoComplete'
    | 'maxLength'
    | 'maxLengthHidden'
    | 'prefix'
    | 'className'
    | 'wrapperClassName'
    | 'inputClassname'
    | 'placeholder'
  > {
  /** a numeric price used in the input's placeholder and initial calculator price */
  placeholder?: string | number;
  error?: string;
  forceInteger?: boolean;
  hidePointsInfo?: boolean;
  onChange: (value: string, errorMessage?: string) => void;
  className?: string;
  inputClassName?: string;
}

export const DollarPriceInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      value = '',
      name = 'price',
      label,
      placeholder = 0,
      postfix,
      details,
      error,
      min = 0,
      max = MAX_CORE_MAX,
      forceInteger,
      hidePointsInfo,
      onChange,

      className,
      inputClassName,
      ...remainingInputProps
    },
    ref
  ) => {
    const { t } = useTranslation();
    const id = useUniqueId();

    const chargedInPoints =
      !!value && +value > 0 && +value < MIN_PRICE_TO_ACCEPT_DOLLARS;
    const chargedInPointsHint = chargedInPoints && !hidePointsInfo && (
      <div className={s['points-hint']}>
        {t('common.dollarPriceInput.warning.pointsOnly', {
          value: MIN_PRICE_TO_ACCEPT_DOLLARS,
        })}{' '}
        <PlainButtonOrLink
          target="_blank"
          href={URLS.ABOUT_POINTS}
          className={s['what-are-points']}
        >
          {t('common.faq.whatArePoints')}
        </PlainButtonOrLink>
      </div>
    );

    const onChangeHandler = useCallback(
      (ev: React.ChangeEvent<HTMLInputElement>) => {
        const numericValue = parseFloat(ev.target.value);

        // Set a limit to how high a price the user is allowed to type.
        // This is separate from the actual field validation which usually takes place in the form.
        const newValue = isNaN(numericValue)
          ? ''
          : numericValue > max
          ? `${max}`
          : forceInteger
          ? numericValue.toFixed(0)
          : ev.target.value;

        onChange(newValue);
      },
      [forceInteger, max, onChange]
    );

    return (
      <div className={classnames(s['root'], className)}>
        <div className={s['price-input-line']}>
          <TextInput
            id={id}
            type="number"
            name={name}
            wrapperClassName={classnames(s['price-input'], inputClassName)}
            value={value}
            validationResult={error ? 'error' : undefined}
            details={error || chargedInPointsHint || details}
            onChange={onChangeHandler}
            prefix={<DollarSignIcon disableDefaultColors />}
            postfix={
              <div className={s['price-input-postfix']}>
                {postfix || t('common.dollarPriceInput.postfix')}
              </div>
            }
            placeholder={
              isNumber(placeholder)
                ? placeholder > 0
                  ? `${placeholder}`
                  : undefined
                : placeholder
            }
            label={label || t('common.dollarPriceInput.inputLabel.v2')}
            min={min}
            max={max}
            step={1}
            maxLength={8} // 10000.00
            maxLengthHidden
            autoComplete="off"
            ref={ref}
            {...remainingInputProps}
          />
        </div>
      </div>
    );
  }
);
DollarPriceInput.displayName = 'DollarPriceInput';

export default DollarPriceInput;
